import { useContext, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import DataContext from "../../../context/DataContext";
import { useRequest } from "../../../hooks/useRequest";
import { useToolsRunTrackedWells } from "../../../hooks/useToolsRunTrackedWells";
import {
  ClientProps,
  COILineItemProps,
  COIRecordType,
  PumpEntryStatus,
} from "../../../interfaces/interfaces";

type NamedOption = { id: number; name: string };
type ProductOption = NamedOption & { partNumber: string };
type FreeOption<T> = T | string | null;

const idFor = <T extends NamedOption>(value: FreeOption<T>) =>
  typeof value === "object" && value ? value.id : undefined;
const createFor = <T extends NamedOption>(value: FreeOption<T>) =>
  typeof value === "string" && value.trim() ? { name: value.trim() } : undefined;

const BUSINESS_STATUS_OPTIONS: { value: PumpEntryStatus | ""; label: string }[] = [
  { value: "", label: "Not set" },
  { value: "in-stock", label: "In stock" },
  { value: "reused", label: "Reused" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  clientOptions: ClientProps[];
  categories: string[];
  initialClientId?: number | null;
  lockedClientId?: number | null;
  editingRow?: COILineItemProps | null;
}

export const COILineItemForm = ({
  open,
  onClose,
  onSaved,
  clientOptions,
  categories,
  initialClientId = null,
  lockedClientId = null,
  editingRow = null,
}: Props) => {
  const { handleRequest } = useRequest();
  const { productOptions } = useContext(DataContext);
  const { wellOptions, fetchDataWells } = useToolsRunTrackedWells();
  const products = productOptions as ProductOption[];
  const wells = useMemo<NamedOption[]>(
    () =>
      wellOptions
        .filter((option) => option.id != null)
        .map((option) => ({ id: option.id as number, name: option.name })),
    [wellOptions],
  );

  const isEdit = Boolean(editingRow);
  const [recordType, setRecordType] = useState<COIRecordType>("inventory");
  const [clientId, setClientId] = useState<number | null>(initialClientId);
  const [partNumber, setPartNumber] = useState("");
  const [equipmentDescription, setEquipmentDescription] = useState("");
  const [equipmentCategory, setEquipmentCategory] = useState("");
  const [quantity, setQuantity] = useState("");
  const [installDate, setInstallDate] = useState("");
  const [pullDate, setPullDate] = useState("");
  const [reinstallDate, setReinstallDate] = useState("");
  const [well, setWell] = useState<FreeOption<NamedOption>>(null);
  const [wellInput, setWellInput] = useState("");
  const [toText, setToText] = useState("");
  const [product, setProduct] = useState<FreeOption<ProductOption>>(null);
  const [productInput, setProductInput] = useState("");
  const [businessStatus, setBusinessStatus] = useState<PumpEntryStatus | "">("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    const startingClientId = editingRow?.clientId ?? lockedClientId ?? initialClientId;
    setRecordType(editingRow?.recordType ?? "inventory");
    setClientId(startingClientId);
    setPartNumber(editingRow?.partNumber ?? "");
    setEquipmentDescription(editingRow?.equipmentDescription ?? "");
    setEquipmentCategory(editingRow?.equipmentCategory ?? "");
    setQuantity(editingRow?.quantity != null ? String(editingRow.quantity) : "");
    setInstallDate(editingRow?.installDate?.slice(0, 10) ?? "");
    setPullDate(editingRow?.pullDate?.slice(0, 10) ?? "");
    setReinstallDate(editingRow?.reinstallDate?.slice(0, 10) ?? "");
    setWell(editingRow?.well || null);
    setWellInput(editingRow?.well?.name || editingRow?.fromWellRaw || "");
    setToText(editingRow?.toText ?? "");
    const selectedProduct =
      (products.find((option) => option.id === editingRow?.productId) as ProductOption | undefined) || null;
    setProduct(selectedProduct);
    setProductInput(
      selectedProduct ? `${selectedProduct.partNumber} · ${selectedProduct.name}` : "",
    );
    setBusinessStatus(editingRow?.businessStatus ?? "");
    setError("");
    if (startingClientId) fetchDataWells(startingClientId);
  };

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingRow]);

  const canSubmit = Boolean(clientId) && Boolean(partNumber.trim());

  const submit = async () => {
    if (!canSubmit || !clientId) return;
    setSaving(true);
    setError("");
    try {
      const effectiveWell = well || (wellInput.trim() ? wellInput.trim() : null);
      const effectiveProduct = product || (productInput.trim() ? productInput.trim() : null);
      const payload = {
        recordType,
        partNumber: partNumber.trim(),
        equipmentDescription: equipmentDescription.trim() || undefined,
        equipmentCategory: equipmentCategory.trim() || undefined,
        quantity: quantity !== "" ? Number(quantity) : undefined,
        installDate: installDate || undefined,
        pullDate: pullDate || undefined,
        fromWellRaw: wellInput.trim() || undefined,
        toText: toText.trim() || undefined,
        reinstallDate: recordType === "reinstall" ? reinstallDate || undefined : undefined,
        wellId: idFor(effectiveWell),
        createWell: createFor(effectiveWell),
        productId: idFor(effectiveProduct),
        createProduct: createFor(effectiveProduct),
        businessStatus: businessStatus || undefined,
      };

      const saveResponse = await handleRequest({
        endpoint: isEdit ? `coi/${editingRow!.id}` : "coi",
        options: {
          method: isEdit ? "PUT" : "POST",
          body: JSON.stringify(isEdit ? payload : { clientId, ...payload }),
        },
      });
      if (!saveResponse.success) {
        throw new Error(saveResponse.error || "Unable to save the record");
      }
      onSaved();
      onClose();
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : "Unable to save the record",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 850 }}>
        {isEdit ? "Edit inventory record" : "Add inventory record"}
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <FormControl fullWidth size="small" margin="normal">
          <InputLabel>Record type</InputLabel>
          <Select
            label="Record type"
            value={recordType}
            onChange={(event) => setRecordType(event.target.value as COIRecordType)}
          >
            <MenuItem value="inventory">Inventory</MenuItem>
            <MenuItem value="reinstall">Reinstall</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          options={clientOptions}
          value={clientOptions.find((client) => client.id === clientId) || null}
          getOptionLabel={(client) => client.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={Boolean(lockedClientId) || isEdit}
          onChange={(_, client) => {
            setClientId(client?.id || null);
            setWell(null);
            setWellInput("");
            if (client?.id) fetchDataWells(client.id);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Client" margin="normal" required />
          )}
        />

        <TextField
          fullWidth
          size="small"
          label="Part number"
          margin="normal"
          required
          value={partNumber}
          onChange={(event) => setPartNumber(event.target.value)}
        />

        <TextField
          fullWidth
          size="small"
          label="Equipment description (optional)"
          margin="normal"
          multiline
          minRows={2}
          value={equipmentDescription}
          onChange={(event) => setEquipmentDescription(event.target.value)}
        />

        <Autocomplete
          freeSolo
          size="small"
          options={categories}
          value={equipmentCategory}
          onInputChange={(_, value) => setEquipmentCategory(value)}
          renderInput={(params) => (
            <TextField {...params} label="Category (optional)" margin="normal" />
          )}
        />

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Qty (optional)"
            margin="normal"
            inputProps={{ min: 0 }}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Install date (optional)"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={installDate}
            onChange={(event) => setInstallDate(event.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Pull date (optional)"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={pullDate}
            onChange={(event) => setPullDate(event.target.value)}
          />
        </Box>

        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          value={well}
          options={wells}
          disabled={!clientId}
          getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
          onChange={(_, value) => {
            setWell(value);
            if (typeof value === "string") setWellInput(value);
          }}
          onInputChange={(_, value, reason) => {
            setWellInput(value);
            if (reason === "input") setWell(null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="From well (optional)"
              margin="normal"
              placeholder={clientId ? "Select or type a well" : "Select a client first"}
            />
          )}
        />

        <TextField
          fullWidth
          size="small"
          label="To (optional)"
          margin="normal"
          value={toText}
          onChange={(event) => setToText(event.target.value)}
        />

        {recordType === "reinstall" ? (
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Reinstall date (optional)"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={reinstallDate}
            onChange={(event) => setReinstallDate(event.target.value)}
          />
        ) : null}

        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          value={product}
          inputValue={productInput}
          options={products}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : `${option.partNumber} · ${option.name}`
          }
          onChange={(_, value) => {
            setProduct(value);
            if (typeof value === "string") setProductInput(value);
          }}
          onInputChange={(_, value, reason) => {
            setProductInput(value);
            if (reason === "input") setProduct(null);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Product (optional)" margin="normal" />
          )}
        />

        <FormControl fullWidth size="small" margin="normal">
          <InputLabel>Business status</InputLabel>
          <Select
            label="Business status"
            value={businessStatus}
            onChange={(event) => setBusinessStatus(event.target.value as PumpEntryStatus | "")}
          >
            {BUSINESS_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value || "unset"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit || saving} onClick={submit}>
          {isEdit ? "Save changes" : "Add record"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
